import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { PaperPlaneTilt, HandsPraying, EnvelopeSimple, WhatsappLogo } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contacto = () => {
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "", tipo: "contacto", mensaje: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(
        form.tipo === "oracion"
          ? "Tu petición fue recibida. Estaremos orando por ti."
          : "Mensaje enviado. Te contactaremos muy pronto."
      );
      setForm({ nombre: "", email: "", telefono: "", tipo: "contacto", mensaje: "" });
    } catch {
      toast.error("No pudimos enviar tu mensaje. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border-b border-white/20 bg-transparent py-4 text-lg font-light tracking-wide text-paper placeholder:font-serif placeholder:italic placeholder:text-paper/35 transition-colors duration-300 focus:border-paper";

  return (
    <section id="contacto" className="relative overflow-hidden border-t border-white/10 bg-surface py-28 lg:py-36" data-testid="contacto-section">
      <span className="outline-text pointer-events-none absolute -left-6 bottom-0 select-none font-serif text-[16vw] leading-none" aria-hidden="true">
        Ora
      </span>
      <div className="relative mx-auto max-w-[1600px] px-6 lg:px-12">
        <div className="grid gap-16 lg:grid-cols-2">
          <div>
            <Reveal>
              <p className="mb-8 flex items-center gap-4 text-[10px] font-light uppercase tracking-[0.5em] text-fire" data-testid="contacto-eyebrow">
                <span className="inline-block h-px w-16 bg-fire" /> Hablemos
              </p>
              <h2 className="font-serif text-5xl leading-[0.95] tracking-tight text-paper sm:text-7xl" data-testid="contacto-title">
                Oramos <em className="italic text-gold">contigo</em>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-10 max-w-md text-sm font-light leading-relaxed tracking-wide text-smoke">
                ¿Tienes una petición de oración, una pregunta o simplemente quieres saludarnos?
                Escríbenos: cada mensaje es leído con cariño por nuestro equipo pastoral.
              </p>
              <div className="mt-12 border-t border-white/10">
                <div className="flex items-center gap-5 border-b border-white/10 py-6" data-testid="contacto-oracion-info">
                  <HandsPraying size={22} weight="light" className="shrink-0 text-fire" />
                  <p className="text-sm font-light text-paper/70">Las peticiones de oración se presentan cada lunes a las 6:00 AM, vía Zoom.</p>
                </div>
                <div className="flex items-center gap-5 border-b border-white/10 py-6" data-testid="contacto-respuesta-info">
                  <EnvelopeSimple size={22} weight="light" className="shrink-0 text-fire" />
                  <p className="text-sm font-light text-paper/70">Respondemos mensajes de contacto en menos de 48 horas.</p>
                </div>
                <a
                  href="https://wa.me/527223947826"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-5 border-b border-white/10 py-6"
                  data-testid="contacto-whatsapp-link"
                >
                  <WhatsappLogo size={22} weight="light" className="shrink-0 text-fire" />
                  <p className="text-sm font-light text-paper/70">
                    WhatsApp directo: <span className="text-paper transition-colors duration-300 group-hover:text-fire">+52 722 394 7826</span>
                  </p>
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <form onSubmit={submit} className="border border-white/10 bg-ink p-8 sm:p-12" data-testid="contacto-form">
              <div className="mb-10 flex gap-px border border-white/15" data-testid="contacto-tipo-selector">
                {[
                  { v: "contacto", label: "Contacto" },
                  { v: "oracion", label: "Petición de oración" },
                ].map((t) => (
                  <button
                    key={t.v}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, tipo: t.v }))}
                    data-testid={`contacto-tipo-${t.v}`}
                    className={`flex-1 px-4 py-3 text-[10px] font-medium uppercase tracking-[0.25em] transition-colors duration-300 ${
                      form.tipo === t.v ? "bg-paper text-ink" : "text-paper/50 hover:text-paper"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="space-y-7">
                <input
                  required
                  value={form.nombre}
                  onChange={set("nombre")}
                  placeholder="Tu nombre *"
                  className={inputCls}
                  data-testid="contacto-input-nombre"
                />
                <div className="grid gap-7 sm:grid-cols-2">
                  <input
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="Correo electrónico"
                    className={inputCls}
                    data-testid="contacto-input-email"
                  />
                  <input
                    value={form.telefono}
                    onChange={set("telefono")}
                    placeholder="Teléfono / WhatsApp"
                    className={inputCls}
                    data-testid="contacto-input-telefono"
                  />
                </div>
                <textarea
                  required
                  rows={4}
                  value={form.mensaje}
                  onChange={set("mensaje")}
                  placeholder={form.tipo === "oracion" ? "¿Por qué podemos orar contigo? *" : "Escribe tu mensaje *"}
                  className={`${inputCls} resize-none`}
                  data-testid="contacto-input-mensaje"
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="contacto-submit-btn"
                className="group mt-12 flex w-full items-center justify-center gap-3 bg-fire py-5 text-[11px] font-medium uppercase tracking-[0.35em] text-ink transition-colors duration-300 hover:bg-paper disabled:opacity-60"
              >
                <PaperPlaneTilt size={15} weight="regular" className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                {sending ? "Enviando..." : form.tipo === "oracion" ? "Enviar petición" : "Enviar mensaje"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contacto;
